import { ChevronDown, ChevronRight, Search, X } from 'lucide-react'
import { useState } from 'react'

import { clases } from '../componentes/ui'
import { empezarArrastre, terminarArrastre } from './arrastre'
import { type Bloque, gruposDeLaPaleta } from './catalogo'
import { IconoDeTipo } from './iconos'

/**
 * La paleta de campos: los tipos que el núcleo ofrece, por grupos plegables (Generales,
 * Avanzados, Estructura, Pago), con un buscador. Pulsar un tipo lo añade; arrastrarlo lo
 * deja donde se suelte en el lienzo. La misma paleta sale en la barra de la derecha y en
 * la ventanita del «+» entre campos.
 */
export const Paleta = ({
  bloques,
  alElegir,
  arrastrable = true,
  autoFoco = false,
  ayuda,
}: {
  alElegir: (tipo: string) => void
  arrastrable?: boolean
  autoFoco?: boolean
  ayuda?: string
  bloques: Bloque[]
}) => {
  const [busqueda, setBusqueda] = useState('')
  const [plegados, setPlegados] = useState<Set<string>>(new Set())
  const grupos = gruposDeLaPaleta(bloques, busqueda)
  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tenue"
        />
        <input
          aria-label="Buscar un campo"
          autoFocus={autoFoco}
          className="campo h-9 w-full rounded-campo border border-linea bg-papel pr-8 pl-9 text-sm text-tinta placeholder:text-tenue hover:border-linea-fuerte"
          data-autofocus={autoFoco ? true : undefined}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar un campo…"
          type="search"
          value={busqueda}
        />
        {busqueda && (
          <button
            aria-label="Borrar la búsqueda"
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-tenue hover:text-tinta"
            onClick={() => setBusqueda('')}
            type="button"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
      {ayuda && <p className="text-xs text-tenue">{ayuda}</p>}
      {grupos.length === 0 && (
        <p className="py-4 text-center text-sm text-tenue">Ningún campo se llama así.</p>
      )}
      {grupos.map(({ grupo, bloques: lista }) => {
        // Buscando, todo abierto: lo que se busca no puede quedar dentro de un grupo plegado.
        const abierto = Boolean(busqueda) || !plegados.has(grupo)
        return (
          <section aria-label={grupo} key={grupo}>
            <button
              aria-expanded={abierto}
              className="flex w-full items-center gap-1.5 rounded-boton py-1 text-left text-xs font-semibold tracking-wide text-tenue uppercase hover:text-tinta"
              onClick={() =>
                setPlegados((p) => {
                  const siguiente = new Set(p)
                  if (siguiente.has(grupo)) siguiente.delete(grupo)
                  else siguiente.add(grupo)
                  return siguiente
                })
              }
              type="button"
            >
              {abierto ? (
                <ChevronDown aria-hidden className="size-3.5" />
              ) : (
                <ChevronRight aria-hidden className="size-3.5" />
              )}
              {grupo}
              <span className="font-normal">· {lista.length}</span>
            </button>
            {abierto && (
              <ul className="mt-1.5 grid grid-cols-2 gap-1.5">
                {lista.map((b) => (
                  <li key={b.slug}>
                    <button
                      className={clases(
                        'group flex w-full items-center gap-2 rounded-boton border border-linea bg-papel px-2.5 py-2 text-left text-[13px] text-tinta shadow-suave transition-colors hover:border-violeta-borde hover:bg-violeta-suave/40',
                        arrastrable && 'cursor-grab active:cursor-grabbing',
                      )}
                      draggable={arrastrable}
                      onClick={() => alElegir(b.slug)}
                      onDragEnd={terminarArrastre}
                      onDragStart={(e) => empezarArrastre(e, { que: 'nuevo', tipo: b.slug })}
                      title={b.descripcion ?? `Añadir «${b.etiqueta}»`}
                      type="button"
                    >
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-papel-3 text-tinta-2 group-hover:bg-violeta-suave group-hover:text-violeta">
                        <IconoDeTipo className="size-3.5" tipo={b.slug} />
                      </span>
                      <span className="min-w-0 leading-tight">{b.etiqueta}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}
